import { create } from 'zustand'
import type { WorkspaceTab } from '../types/editor.types'

interface WorkspaceState {
  tabs: WorkspaceTab[]
  activeTabId: string | null

  openTab: (tab: WorkspaceTab) => void
  closeTab: (id: string) => void
  /** Close all tabs tied to nodeId */
  closeTabsByNodeId: (nodeId: string) => void
  setActiveTab: (id: string | null) => void
  setTabDirty: (id: string, isDirty: boolean) => void
  setTabEditorState: (id: string, state: unknown) => void
  getActiveTab: () => WorkspaceTab | null
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  tabs: [],
  activeTabId: null,

  openTab: (tab) => {
    set((state) => {
      const existingIndex = state.tabs.findIndex((t) => t.id === tab.id)
      if (existingIndex >= 0) {
        // Update existing tab's nodeId in case it changed (for multi-sheet scenarios)
        const updatedTabs = [...state.tabs]
        updatedTabs[existingIndex] = { ...updatedTabs[existingIndex], nodeId: tab.nodeId }
        return { tabs: updatedTabs, activeTabId: tab.id }
      }
      return {
        tabs: [...state.tabs, tab],
        activeTabId: tab.id,
      }
    })
  },
  closeTab: (id) => {
    set((state) => {
      const idx = state.tabs.findIndex((t) => t.id === id)
      if (idx < 0) return state
      const tabs = state.tabs.filter((t) => t.id !== id)
      const nextActive =
        state.activeTabId === id
          ? tabs[idx]?.id ?? tabs[idx - 1]?.id ?? null
          : state.activeTabId
      return { tabs, activeTabId: nextActive }
    })
  },
  closeTabsByNodeId: (nodeId) => {
    set((state) => {
      const toRemove = new Set(state.tabs.filter((t) => t.nodeId === nodeId).map((t) => t.id))
      if (toRemove.size === 0) return state
      const tabs = state.tabs.filter((t) => !toRemove.has(t.id))
      const nextActive = toRemove.has(state.activeTabId!)
        ? tabs[0]?.id ?? null
        : state.activeTabId
      return { tabs, activeTabId: nextActive }
    })
  },
  setActiveTab: (activeTabId) => set({ activeTabId }),
  setTabDirty: (id, isDirty) => {
    set((state) => ({
      tabs: state.tabs.map((t) =>
        t.id === id ? { ...t, isDirty } : t
      ),
    }))
  },
  setTabEditorState: (id, editorState) => {
    set((state) => ({
      tabs: state.tabs.map((t) =>
        t.id === id ? { ...t, editorState } : t
      ),
    }))
  },
  getActiveTab: () => {
    const { tabs, activeTabId } = get()
    return tabs.find((t) => t.id === activeTabId) ?? null
  },
}))
